/**
 * featureFlags.ts
 *
 * This file maps client feature flags for the front-end.
 *
 * Feature flags can be opted in for client use by mapping them to
 * an enum value in ClientFeatureFlag.cs. The feature flag export here
 * should check if the clientFeatureFlags array includes the exact name
 * of the middle-tier ClientFeatureFlag enum value (PascalCase).
 *
 * These feature flags are transmitted to the client via JWT claims cookie
 * (ff claim). The cookies token value is base64 decoded on the client to
 * attain the feature flags synchronously.
 *
 * When running development build feature flags state can be queried via
 * chrome developer console by executing `featureFlags` (global).
 */

import { getSessionClaims } from "./sessionClaims";

// (b/153007582): This will only work for routes receiving the JWT.
// When the feature above is implemented, this can be used to detect
// ClientFeatureFlag across Kaggle.
let flagsCache: any;
const getFeatureFlags = () => {
  if (!flagsCache) {
    flagsCache = getSessionClaims()?.ff || [];
  }
  return flagsCache;
};

// Using 'class' for getters.
class FeatureFlags {
  /** The gcloud-kernel-ui FF. */
  get gcloudKernelInteg() {
    return getFeatureFlags().includes("GcloudKernelInteg");
  }

  /** The tpu-unused-nudge-ui FF. */
  get tpuUnusedNudge() {
    return getFeatureFlags().includes("TpuUnusedNudge");
  }

  /** The caip-export-ui FF. */
  get caipExport() {
    return getFeatureFlags().includes("CaipExport");
  }

  /** The caip-nudge-ui FF. */
  get caipNudge() {
    return getFeatureFlags().includes("CaipNudge");
  }

  /** Shows the new Docker Modal selector */
  get dockerModalSelector() {
    return getFeatureFlags().includes("DockerModalSelector");
  }

  /** The kernels-firebase-proxy FF. */
  get kernelsFirebaseProxy() {
    return getFeatureFlags().includes("KernelsFirebaseProxy");
  }

  /** The kernels-firebase-long-polling FF. */
  get kernelsFirebaseLongPolling() {
    return getFeatureFlags().includes("KernelsFirebaseLongPolling");
  }

  /** The kernels-firestore-debug-logging FF. */
  get kernelsFirestoreDebugLogging() {
    return getFeatureFlags().includes("KernelsFirestoreDebugLogging");
  }

  /** Enables an optimized client request flow for ListKernels. b/190738176 */
  get kernelsListingListKernelsOptimization() {
    return getFeatureFlags().includes("KernelsListingListKernelsOptimization");
  }

  /** The kernels-prevent-stopped-to-starting-transition FF. */
  get kernelsPreventStoppedToStartingTransition() {
    return getFeatureFlags().includes(
      "KernelsPreventStoppedToStartingTransition"
    );
  }

  /** The kernels-poll-quota FF. */
  get kernelsPollQuota() {
    return getFeatureFlags().includes("KernelsPollQuota");
  }

  /** The kernels-quota-modals FF. */
  get kernelsQuotaModals() {
    return getFeatureFlags().includes("KernelsQuotaModals");
  }

  get kernelEditorCorgiMode() {
    return getFeatureFlags().includes("KernelEditorCorgiMode");
  }

  /** The active-events FF. */
  get activeEvents() {
    return getFeatureFlags().includes("ActiveEvents");
  }

  /** The github-private-access FF. */
  get gitHubPrivateAccess() {
    return getFeatureFlags().includes("GitHubPrivateAccess");
  }

  /** Show the data explorer tree on the left. (A/B test)  FF. */
  get datasetsDataExplorerV3TreeLeft() {
    return getFeatureFlags().includes("DatasetsDataExplorerV3TreeLeft");
  }

  /** Show the topical dataset V1 FF. */
  get datasetsPrependTopicalDatasetsInPublicList() {
    return getFeatureFlags().includes(
      "DatasetsPrependTopicalDatasetsInPublicList"
    );
  }

  get avatarProfilePreview() {
    return getFeatureFlags().includes("AvatarProfilePreview");
  }

  get kernelsSnippetEditor() {
    return getFeatureFlags().includes("KernelsSnippetEditor");
  }

  /** Enable "Check for updates" menu item in DataExplorerV3 in Notebook editor. */
  get datasetsDataExplorerV3CheckForUpdates() {
    return getFeatureFlags().includes("DatasetsDataExplorerV3CheckForUpdates");
  }

  /** Enable automatic dataset update check feature in DataExplorer in Notebook editor. */
  get datasetsDataExplorerV3CheckForUpdatesInBackground() {
    return getFeatureFlags().includes(
      "DatasetsDataExplorerV3CheckForUpdatesInBackground"
    );
  }

  /** Surfaces a button in kernel errors to search stack overflow. */
  get kernelsStackOverflowSearch() {
    return getFeatureFlags().includes("KernelsStackOverflowSearch");
  }

  /** Uses the new Material Listing for the /notebooks page. */
  get kernelsMaterialListing() {
    return getFeatureFlags().includes("KernelsMaterialListing");
  }

  /** Uses the new Material Listing for the /datasets page. */
  get datasetsMaterialListing() {
    return getFeatureFlags().includes("DatasetsMaterialListing");
  }

  /** Uses the new Material detail page. */
  get datasetsMaterialDetail() {
    return getFeatureFlags().includes("DatasetsMaterialDetail");
  }

  /** Uses the new Material Dataset List component in existing contexts. */
  get datasetsMaterialListComponent() {
    return getFeatureFlags().includes("DatasetsMaterialListComponent");
  }

  /** Datasets listing on Competition Page */
  get competitionDatasets() {
    return getFeatureFlags().includes("CompetitionDatasets");
  }

  /** Shows an upvote begging warning in the discussions editor. */
  get discussionsUpvoteSpamWarning() {
    return getFeatureFlags().includes("DiscussionsUpvoteSpamWarning");
  }

  /** Whether to show the experimental tags UI. */
  get tagsExperimentUI() {
    return getFeatureFlags().includes("TagsExperimentUI");
  }

  get tagsLearnAndDiscussionsUI() {
    return getFeatureFlags().includes("TagsLearnAndDiscussionsUI");
  }

  /** No Reload Experiment FF for b/161977491 */
  get noReloadExperiment() {
    return getFeatureFlags().includes("NoReloadExperiment");
  }

  /** Enables submit to competitions from the kernels editor sidebar for b/191276057 */
  get kernelsSubmitFromEditor() {
    return getFeatureFlags().includes("KernelsSubmitFromEditor");
  }

  /** Enables the Notebooks Landing Page on /notebooks. */
  get notebooksLandingPage() {
    return getFeatureFlags().includes("NotebooksLandingPage");
  }

  /** Allow Datasets Creation from GCS paths. */
  get datasetsFromGcs() {
    return getFeatureFlags().includes("DatasetsFromGcs");
  }

  /** Enables the TPU commit scheduling. */
  get tpuCommitScheduling() {
    return getFeatureFlags().includes("TPUCommitScheduling");
  }

  /** Enables commit scheduling. */
  get commitScheduling() {
    return getFeatureFlags().includes("CommitScheduling");
  }

  /**
   * Allows forum attachments. When disabled, a pop-up message is shown
   * when the attachment button is visible and clicked.
   */
  get allowForumAttachments() {
    return getFeatureFlags().includes("AllowForumAttachments");
  }

  /** Kaggle Pro */
  get kagglePro() {
    return getFeatureFlags().includes("KagglePro");
  }

  /** KernelsSaveCellOutput */
  get kernelsSaveCellOutput() {
    return getFeatureFlags().includes("KernelsSaveCellOutput");
  }

  /** Enable KM Learn landing page */
  get learnKmLanding() {
    return getFeatureFlags().includes("LearnKmLanding");
  }

  /** Enable nextjs loggedout homepage */
  get nextLoggedoutHomepage() {
    return getFeatureFlags().includes("NextLoggedoutHomepage");
  }

  /** Enable less rapid kernel auto saves */
  get kernelsLessRapidAutoSave() {
    return getFeatureFlags().includes("KernelsLessRapidAutoSave");
  }

  /** Enable KM Learn detail page */
  get kmLearnDetail() {
    return getFeatureFlags().includes("KMLearnDetail");
  }

  /** Enable bookmarks UI */
  get bookmarksUI() {
    return getFeatureFlags().includes("BookmarksUI");
  }

  /** Enable bookmarks UI on Competitions */
  get bookmarksCompsUI() {
    return getFeatureFlags().includes("BookmarksCompsUI");
  }

  /** Enables employer info nudges. */
  get employerNudges() {
    return getFeatureFlags().includes("EmployerInfoNudges");
  }

  /** Enables email sign-up nudges. */
  get emailNudges() {
    return getFeatureFlags().includes("EmailSignupNudges");
  }

  /** Enable KM Competitions Landing page */
  get kmCompsLanding() {
    return getFeatureFlags().includes("CompetitionsKmLanding");
  }

  /** Enable frontend JS error reporting to Stackdriver. */
  get frontendErrorReporting() {
    return getFeatureFlags().includes("FrontendErrorReporting");
  }

  /**
   * Enable frontend JS error reporting through console.error to Stackdriver.
   * Requires EnableFrontendErrorReporting to have any functionality.
   */
  get frontendConsoleErrorReporting() {
    return getFeatureFlags().includes("FrontendConsoleErrorReporting");
  }

  /**
   * If present, will hide the fake exit log line time in the notebook viewer.
   * http://b/177900130
   */
  get kernelViewerHideFakeExitLogTime() {
    return getFeatureFlags().includes("KernelViewerHideFakeExitLogTime");
  }

  /** A/B test to make Datasets shelves rotating. */
  get datasetLandingPageRotatingShelves() {
    return getFeatureFlags().includes("DatasetLandingPageRotatingShelves");
  }

  /** Lower the dataset header image minimum resolution */
  get lowerDatasetHeaderImageMinRes() {
    return getFeatureFlags().includes("LowerDatasetHeaderImageMinRes");
  }

  /** Show the new discussions landing page. */
  get newDiscussionsLanding() {
    return getFeatureFlags().includes("NewDiscussionsLanding");
  }

  /** Updates to topic listing. */
  get discussionListingImprovements() {
    return getFeatureFlags().includes("DiscussionListingImprovements");
  }

  get registrationNewsEmailSignupIsOptOut() {
    return getFeatureFlags().includes("RegistrationNewsEmailSignupIsOptOut");
  }

  get kmLearnLandingTest() {
    return getFeatureFlags().includes("KMLearnLandingTest");
  }

  get kmLearnLandingTestVersionB() {
    return getFeatureFlags().includes("KMLearnLandingTestVersionB");
  }
}

export const featureFlags = new FeatureFlags();

// Allow feature flag inspection via console in dev.
if (process.env.NODE_ENV === "development") {
  (window as any).featureFlags = featureFlags;
}
